import React, { useState, useEffect } from 'react';
import { motion, useMotionValue, animate } from 'motion/react';

export interface AnimatedCountUpProps {
  value: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
  decimals?: number;
}

export const AnimatedCountUp: React.FC<AnimatedCountUpProps> = ({
  value,
  prefix = '',
  suffix = '',
  duration = 1.2,
  decimals = 0,
}) => {
  const count = useMotionValue(0);
  const [displayValue, setDisplayValue] = useState(`${prefix}0${suffix}`);

  useEffect(() => {
    const controls = animate(count, value, {
      duration,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (latest) => {
        const formatted = decimals > 0 ? latest.toFixed(decimals) : Math.round(latest).toString();
        setDisplayValue(`${prefix}${formatted}${suffix}`);
      },
    });
    return () => controls.stop();
  }, [value, prefix, suffix, duration, decimals]);

  return <span>{displayValue}</span>;
};

export interface AnimatedMetricCardProps {
  label: string;
  value: number | string;
  numericValue?: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  icon?: React.ReactNode;
  trend?: React.ReactNode;
  sublabel?: string;
  badge?: React.ReactNode;
  delay?: number;
  colorScheme?: 'lime' | 'amber' | 'cyan' | 'rose' | 'indigo' | 'emerald';
  className?: string;
  onClick?: () => void;
}

export const AnimatedMetricCard: React.FC<AnimatedMetricCardProps> = ({
  label,
  value,
  numericValue,
  prefix = '',
  suffix = '',
  decimals = 0,
  icon,
  trend,
  sublabel,
  badge,
  delay = 0.1,
  colorScheme = 'lime',
  className = '',
  onClick,
}) => {
  const parsedNumeric = numericValue !== undefined ? numericValue : typeof value === 'number' ? value : parseFloat(String(value));
  const isNumber = !isNaN(parsedNumeric);

  const themeClasses = {
    lime: 'border-lime-400/30 hover:border-lime-400/60 bg-indigo-950/80 text-lime-400',
    amber: 'border-amber-400/30 hover:border-amber-400/60 bg-indigo-950/80 text-amber-300',
    cyan: 'border-cyan-400/30 hover:border-cyan-400/60 bg-indigo-950/80 text-cyan-300',
    rose: 'border-rose-400/30 hover:border-rose-400/60 bg-indigo-950/80 text-rose-300',
    indigo: 'border-indigo-400/30 hover:border-indigo-400/60 bg-indigo-950/80 text-indigo-200',
    emerald: 'border-emerald-400/30 hover:border-emerald-400/60 bg-indigo-950/80 text-emerald-300',
  }[colorScheme];

  return (
    <motion.div
      initial={{ opacity: 0, y: 18, scale: 0.95 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: '-20px' }}
      transition={{ duration: 0.45, delay, ease: [0.16, 1, 0.3, 1] }}
      onClick={onClick}
      className={`p-4 rounded-2xl border shadow-lg flex flex-col justify-between transition-all duration-300 relative overflow-hidden ${themeClasses} ${className} ${
        onClick ? 'cursor-pointer hover:scale-[1.02] active:scale-[0.98]' : ''
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[10px] text-indigo-300/80 font-black uppercase tracking-wider flex items-center gap-1.5">
          {icon}
          <span>{label}</span>
        </span>
        {badge}
      </div>

      <div className="flex items-baseline justify-between gap-2 mt-1">
        <div className="text-2xl sm:text-3xl font-black italic tracking-tight text-white">
          {isNumber ? (
            <AnimatedCountUp value={parsedNumeric} prefix={prefix} suffix={suffix} decimals={decimals} />
          ) : (
            <span>{value}</span>
          )}
        </div>
        {trend && <div className="shrink-0">{trend}</div>}
      </div>

      {sublabel && (
        <span className="text-[10px] text-indigo-300/60 font-mono mt-1 block">
          {sublabel}
        </span>
      )}
    </motion.div>
  );
};
